import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { useState } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    Link,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { TaskCategory, TaskStatus } from "../../types/tasks.types";
import useToast from "../../../../hooks/useToast";
import { useDeleteTasks, useUpdateTask } from "../../hooks/tasks.mutations";
import { ViewEditorText } from "../../../../components/TextEditor/ViewEditorText/ViewEditorText";
import { AddTextEditor } from "../../../../components/TextEditor/AddTextEditor/AddTextEditor";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import ReassignTask from "../ReassignTask/ReassignTask";
import { useAppSelector } from "../../../../hooks/useRedux";
import CancelSessionForm from "../../../schedule/components/ClinicianSchedule/CaseloadView/CancelSessionForm/CancelSessionForm";
import { ITask } from "../../../../utils/types/tasks";
import TaskDetails from "../TaskDetails/TaskDetails";
import { cn, formatDate, hasTimePassed } from "../../../../utils/helpers";
import CheckIcon from "../../../../icons/Check";
import DeleteIcon from "../../../../icons/Delete";
import EditIcon from "../../../../icons/Edit";
import UserXIcon from "../../../../icons/UserX";
import { getStatusTag, userIsTasKViewer } from "../../helpers/tasks.helper";

interface TaskProps {
    task: ITask;
}

type Dialogs =
    | "mark_as_completed"
    | "task_details"
    | "delete_task"
    | "forward_task"
    | "reassign_task"
    | "cancel_session"
    | "terminate_client";

export default function Task({ task }: TaskProps) {
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [taskDescription, setTaskDescription] = useState(
        () => task.description || ""
    );

    const [searchParams, setSearchParams] = useSearchParams();

    const params = useParams();

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const loggedInUser = useFetchUserDetails();

    const updateTask = useUpdateTask();
    const deleteTask = useDeleteTasks();

    const { practice } = useAppSelector((state) => state.userPractice);

    const navigate = useNavigate();

    const updateTaskIsMutating = useIsMutating(["update-todo"]);
    const isCancelSessionMutating = useIsMutating(["cancel-session"]);

    const isTaskViewer = userIsTasKViewer(task, loggedInUser.data?.user_id);
    const isTaskCompleted = task.status === TaskStatus.COMPLETED;

    const handleDialogInView = (dialog: Dialogs | null) => {
        setDialogInView(dialog);
    };

    const getDueDateBadgeType = () => {
        if (isTaskCompleted) return "neutral";

        return hasTimePassed(task.due_date) ? "danger" : "warning";
    };

    const getTaskLinkRoute = () => {
        if (
            task.status === TaskStatus.IN_PROGRESS &&
            task.category === TaskCategory.WRITE_SESSION_NOTE
        ) {
            return `/notes?status[]=draft`;
        }

        if (task.category === TaskCategory.WRITE_SESSION_NOTE) {
            return `/add-note?session_history_id=${task?.metadata?.session_history_id}`;
        }

        if (task.category === TaskCategory.MAKE_REQUESTED_CHANGE) {
            return `/notes/${task?.metadata?.client_id}/${task?.metadata?.note_id}?note_tab=change_requested`;
        }

        if (task.category === TaskCategory.REVIEW_SUPERVISEE_ADDENDUM) {
            return `/notes/${task?.metadata?.client_id}/${task?.metadata?.note_id}`;
        }

        if (task.category === TaskCategory.REVIEW_SUPERVISEE_NOTE) {
            return `/notes/${task?.metadata?.client_id}/${task?.metadata?.note_id}`;
        }

        if (task.category === TaskCategory.WRITE_SUPERVISION_NOTE) {
            return `/add-note?session_history_id=${task?.metadata?.session_history_id}&show_billable=false&note_type=supervision_note`;
        }

        if (task.category === TaskCategory.WRITE_SUPERVISOR_CHECK_IN_NOTE) {
            return `/add-note?session_history_id=${task?.metadata?.session_history_id}&show_billable=false&note_type=supervisor_check_in`;
        }

        if (
            task.category === TaskCategory.USER_CREATED &&
            task?.metadata?.client_id &&
            !params.clientId
        ) {
            return `/clients/${task?.metadata?.client_id}`;
        }

        return "";
    };

    const taskLinkUrl = getTaskLinkRoute();

    const handleOpenTaskDetailsDialog = () => {
        searchParams.set("task_id", task.todo_id);
        setSearchParams(searchParams);
        handleDialogInView("task_details");
    };

    const handleCloseDetailsDialog = () => {
        searchParams.delete("task_id");
        setSearchParams(searchParams, { replace: true });
        handleDialogInView(null);
    };

    const handleUpdateTask = (payload: {
        todoId: string;
        status?: TaskStatus;
        description?: string;
    }) => {
        const dataToSend = {
            todo_id: payload.todoId,
            status: payload.status || undefined,
            description: payload.description || undefined,
        };

        if (isEditing && !taskDescription) {
            toast({
                mode: "error",
                message: "Task description field cannot be empty",
            });
            return;
        }

        updateTask.mutate(
            { todos_to_update: [dataToSend] },
            {
                onSuccess: (response) => {
                    if (
                        response.data.failed_todo_ids.includes(payload.todoId)
                    ) {
                        toast({
                            mode: "error",
                            message: "Could not update task at this time",
                        });
                        return;
                    }

                    toast({
                        mode: "success",
                        message: "Task updated successfully",
                    });
                    setDialogInView(null);
                    queryClient.invalidateQueries({
                        queryKey: ["todos"],
                    });
                    if (isEditing) {
                        setIsEditing(false);
                        setTaskDescription("");
                    }
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update task at this time",
                    });
                },
            }
        );
    };

    const handleDeleteTask = () => {
        deleteTask.mutate([task.todo_id], {
            onSuccess: () => {
                toast({
                    mode: "success",
                    message: "Task deleted successfully",
                });
                handleDialogInView(null);
                queryClient.invalidateQueries({
                    queryKey: ["todos"],
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete task at this time",
                });
            },
        });
    };

    return (
        <>
            <Dialog open={dialogInView === "task_details"}>
                <DialogContent
                    title="Task Details"
                    handleCloseDialog={handleCloseDetailsDialog}
                    submitBtnFormValue="edit-task"
                    isSubmitBtnDisabled={updateTaskIsMutating > 0}
                    isCancelBtnDisabled={updateTaskIsMutating > 0}
                    showFooter={false}
                >
                    <TaskDetails />
                </DialogContent>
            </Dialog>
            <AlertDialog open={dialogInView === "mark_as_completed"}>
                <AlertDialogContent
                    onSaveClick={() =>
                        handleUpdateTask({
                            todoId: task.todo_id,
                            status: TaskStatus.COMPLETED,
                        })
                    }
                    onCancelClick={() => setDialogInView(null)}
                    isCancelBtnDisabled={updateTask.isLoading}
                    isSaveBtnDisabled={updateTask.isLoading}
                    title="Are you sure you want to mark task as complete"
                    description=" By completing this task, you won’t be able to
                    recover task as you have marked and completed the
                    task."
                />
            </AlertDialog>
            <AlertDialog open={dialogInView === "delete_task"}>
                <AlertDialogContent
                    onSaveClick={handleDeleteTask}
                    onCancelClick={() => handleDialogInView(null)}
                    isCancelBtnDisabled={deleteTask.isLoading}
                    isSaveBtnDisabled={deleteTask.isLoading}
                    saveText="Delete task"
                    isDeleting
                    title="Are you sure you want to delete this task?"
                    description="By deleting this task, you won’t be able to recover
                    task and this action can’t be undone"
                />
            </AlertDialog>
            <Dialog
                open={
                    dialogInView === "forward_task" ||
                    dialogInView === "reassign_task"
                }
            >
                <DialogContent
                    title={
                        dialogInView === "forward_task"
                            ? "Forward task"
                            : "Re-assign task"
                    }
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    submitBtnFormValue="reassign-task"
                    isCancelBtnDisabled={updateTaskIsMutating > 0}
                    isSubmitBtnDisabled={updateTaskIsMutating > 0}
                >
                    <ReassignTask
                        onFormSubmit={() => handleDialogInView(null)}
                        action={
                            dialogInView === "forward_task"
                                ? "forward"
                                : "reassign"
                        }
                        task={task}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "cancel_session"}>
                <DialogContent
                    title="Cancel session"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => handleDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    canCancelButtonSubmit
                    onCancelClick={() => setShouldNavigate(false)}
                    cancelText="Yes,cancel"
                    saveText="No,create No show note"
                    cancelBtnFormValue="cancel_client_session"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => {
                            handleDialogInView(null);
                        }}
                        sessionHistoryId={
                            task?.metadata?.session_history_id as string
                        }
                        shouldNavigate={shouldNavigate}
                        action="cancel"
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "terminate_client"}>
                <DialogContent
                    title="Terminate Client"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => handleDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    onCancelClick={() => handleDialogInView(null)}
                    cancelText="Cancel"
                    saveText="Terminate & Write Note"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => {
                            handleDialogInView(null);
                        }}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={
                            task?.metadata?.session_history_id as string
                        }
                        externalSessionId={task?.external_session_id || ""}
                        intakeClientId={task?.client?.intake_client_id || null}
                        action="terminate"
                    />
                </DialogContent>
            </Dialog>
            <div
                className={cn(
                    "px-[--padding-card-x] py-[--padding-card-y] rounded-md flex gap-x-[--spacing-items] items-start",
                    isTaskCompleted
                        ? "bg-[--color-success-bg]"
                        : "[html[data-theme='h-green']_&]:bg-[#FFEBED] [html[data-theme='light']_&]:bg-[rgba(255,235,237,0.32)]"
                )}
            >
                <div>
                    {isTaskCompleted && (
                        <span className="size-5 flex items-center">
                            <CheckIcon className="size-4 stroke-[--color-success-text]" />
                        </span>
                    )}
                    {!isTaskCompleted && !isTaskViewer && (
                        <ActionButton
                            variant="normal"
                            size="auto"
                            className="size-5 flex items-center"
                            disabled={updateTask.isLoading}
                            onClick={() => setDialogInView("mark_as_completed")}
                            actionTitle="Mark as completed"
                            aria-label="Mark as completed"
                        >
                            <span className="inline-flex size-4 rounded-full [border:2px_solid_rgba(0,0,0,0.2)] bg-transparent" />
                        </ActionButton>
                    )}
                </div>
                <div className="flex flex-col items-start w-full">
                    <div className="mb-1 text-black">
                        {task.category === TaskCategory.USER_CREATED ? (
                            <ActionButton
                                variant="normal"
                                size="auto"
                                className="!font-medium leading-none
                                tracking-[-0.084px]  underline *:!text-[0.9375rem] *:!text-[#0b132b] *:!m-0 [text-decoration-color:#0b132b]"
                                onClick={handleOpenTaskDetailsDialog}
                                actionTitle="View details"
                            >
                                {task.title ? (
                                    <ViewEditorText text={task.title} />
                                ) : (
                                    "--"
                                )}
                            </ActionButton>
                        ) : (
                            <Link
                                to={taskLinkUrl}
                                className="*:!text-[0.9375rem] !font-medium
                                tracking-[-0.084px] *:!text-[#0b132b] underline *:!m-0 [text-decoration-color:#0b132b]"
                            >
                                {task.title ? (
                                    <ViewEditorText text={task.title} />
                                ) : (
                                    "--"
                                )}
                            </Link>
                        )}
                    </div>
                    <div className="w-full">
                        {isEditing ? (
                            <>
                                <AddTextEditor
                                    defaultValue={taskDescription || ""}
                                    onEditorTextChange={setTaskDescription}
                                    isRequiredFieldProvided={Boolean(
                                        taskDescription
                                    )}
                                    reduceToolbaroptions
                                    editorClass="!h-[70px] !bg-white"
                                />

                                <div className="flex justify-end gap-2 mt-[--spacing-items]">
                                    <Button
                                        variant="secondary"
                                        aria-label="Cancel"
                                        onClick={() => setIsEditing(false)}
                                        disabled={updateTask.isLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        onClick={() =>
                                            handleUpdateTask({
                                                todoId: task.todo_id,
                                                description: taskDescription,
                                            })
                                        }
                                        disabled={updateTask.isLoading}
                                    >
                                        Save changes
                                    </Button>
                                </div>
                            </>
                        ) : (
                            (task?.description && (
                                <div className="*:!text-caption *:!font-normal tracking-[-0.084px] *:!mb-0 *:!text-[#576a81]">
                                    <ViewEditorText text={task.description} />
                                </div>
                            )) ||
                            "--"
                        )}
                    </div>
                    <div className="flex flex-col min-[500px]:flex-row gap-y-3 flex-wrap items-start min-[500px]:items-center justify-between gap-x-1 mt-[--spacing-items] w-full">
                        <div className="flex flex-wrap items-center gap-1">
                            <Badge
                                content={`Due date: ${formatDate(
                                    task.due_date
                                )}`}
                                type={getDueDateBadgeType()}
                            />
                            {getStatusTag(task.status)}
                        </div>
                        {!isTaskViewer ? (
                            <div className="px-2 py-1 min-h-7 flex items-center gap-x-1.5 rounded-md bg-white [border] shadow-[0px_6px_10px_0px_rgba(14,18,27,0.06),_0px_2px_4px_0px_rgba(14,18,27,0.03)] flex-wrap gap-y-2">
                                {!task.is_system_created && (
                                    <ActionButton
                                        className="px-6 h-6 hover:bg-[#e7e8ec]"
                                        variant="normal"
                                        size="auto"
                                        onClick={() => setIsEditing(true)}
                                        disabled={updateTask.isLoading}
                                        actionTitle="Edit"
                                    >
                                        <EditIcon className="stroke-[#5A5F6A]" />
                                    </ActionButton>
                                )}

                                {loggedInUser.data?.user_id ===
                                task.creator.user_id ? (
                                    <ActionButton
                                        className="px-6 h-6 hover:bg-[#e7e8ec]"
                                        variant="normal"
                                        size="auto"
                                        onClick={() =>
                                            handleDialogInView("delete_task")
                                        }
                                        actionTitle="Delete task"
                                    >
                                        <DeleteIcon className="stroke-[#5A5F6A]" />
                                    </ActionButton>
                                ) : null}

                                <ActionButton
                                    className="px-6 h-6 hover:bg-[#e7e8ec]"
                                    variant="normal"
                                    size="auto"
                                    onClick={() =>
                                        handleDialogInView("forward_task")
                                    }
                                    actionTitle="Forward task"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-4 stroke-[#5A5F6A]"
                                    >
                                        <polyline points="15 14 20 9 15 4" />
                                        <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
                                    </svg>
                                </ActionButton>

                                <ActionButton
                                    className="px-6 h-6 hover:bg-[#e7e8ec]"
                                    variant="normal"
                                    size="auto"
                                    onClick={() =>
                                        handleDialogInView("reassign_task")
                                    }
                                    actionTitle="Reassign task"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 stroke-[#5A5F6A]"
                                    >
                                        <polyline points="23 4 23 10 17 10" />
                                        <polyline points="1 20 1 14 7 14" />
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                    </svg>
                                </ActionButton>

                                {practice?.provider_id &&
                                task?.metadata &&
                                task.metadata.session_history_id ? (
                                    <ActionButton
                                        className="px-6 h-6 hover:bg-[#e7e8ec]"
                                        variant="normal"
                                        size="auto"
                                        onClick={() =>
                                            handleDialogInView("cancel_session")
                                        }
                                        actionTitle="Report session as cancelled"
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="24"
                                            height="24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="size-3.5 stroke-[#5A5F6A]"
                                        >
                                            <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2" />
                                            <line
                                                x1="15"
                                                y1="9"
                                                x2="9"
                                                y2="15"
                                            />
                                            <line
                                                x1="9"
                                                y1="9"
                                                x2="15"
                                                y2="15"
                                            />
                                        </svg>
                                    </ActionButton>
                                ) : null}

                                {practice?.provider_id &&
                                task?.metadata &&
                                task.metadata.session_history_id ? (
                                    <ActionButton
                                        className="px-6 h-6 bg-danger hover:brightness-90"
                                        variant="normal"
                                        size="auto"
                                        onClick={() =>
                                            navigate(
                                                `/add-note?note_type=termination_note&session_history_id=${task?.metadata?.session_history_id}`
                                            )
                                        }
                                        actionTitle="Terminate client"
                                    >
                                        <UserXIcon className="stroke-white" />
                                    </ActionButton>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
}
